import React, { useState } from 'react';
import { useAppStore } from '@/store';
import { useToast, Button, Modal } from '../shared';
import { exportState, importState, downloadBackup, triggerFileUpload } from '@/persistence/exportImport';

export interface ExportImportBarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ExportImportBar({ isOpen, onClose }: ExportImportBarProps): React.ReactElement {
  const { addToast } = useToast();
  const store = useAppStore;
  const [confirmingImport, setConfirmingImport] = useState(false);
  const [pendingBlob, setPendingBlob] = useState<Blob | null>(null);

  const handleExport = () => {
    const state = store.getState();
    const data = {
      schemaVersion: 2,
      courses: state.courses,
      policies: state.policies,
      absenceEntries: state.entries,
      terms: state.terms,
      settings: state.settings,
      budgetAdjustments: state.budgetAdjustments,
      participationEntries: state.participationEntries,
      feedbackEntries: state.feedbackEntries,
      exportedAt: new Date().toISOString(),
    };
    const blob = exportState(data);
    downloadBackup(blob);
    addToast({ severity: 'success', message: 'Backup exported successfully!' });
  };

  const handleImportClick = async () => {
    try {
      const blob = await triggerFileUpload();
      setPendingBlob(blob);
      setConfirmingImport(true);
    } catch {
      addToast({ severity: 'error', message: 'File selection cancelled.' });
    }
  };

  const handleConfirmImport = async () => {
    if (!pendingBlob) return;
    try {
      const data = await importState(pendingBlob);
      store.getState().importState(data);
      addToast({ severity: 'success', message: 'Data imported successfully!' });
    } catch (err) {
      addToast({ severity: 'error', message: `Import failed: ${(err as Error).message}` });
    }
    setConfirmingImport(false);
    setPendingBlob(null);
    onClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Export / Import">
        <div className="export-import-bar">
          <div className="export-import-bar__section">
            <h4>Export Backup</h4>
            <p>Download all your data as a JSON file.</p>
            <Button variant="primary" onClick={handleExport}>Export Data</Button>
          </div>
          <hr />
          <div className="export-import-bar__section">
            <h4>Import Backup</h4>
            <p>Restore data from a previously exported file. This will overwrite your current data.</p>
            <Button variant="danger" onClick={handleImportClick}>Import Data</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={confirmingImport} onClose={() => setConfirmingImport(false)} title="Confirm Import" size="sm">
        <p>Importing will <strong>replace all current data</strong>. Are you sure?</p>
        <div className="export-import-bar__confirm-actions">
          <Button variant="danger" onClick={handleConfirmImport}>Yes, Import</Button>
          <Button variant="secondary" onClick={() => setConfirmingImport(false)}>Cancel</Button>
        </div>
      </Modal>
    </>
  );
}
